import { LandingHeader } from './LandingHeader'
import { LandingFooter } from './LandingFooter'

/**
 * Operator info / "Legal Notice" page. Deliberately NOT named "Impressum"
 * because the German TMG § 5 frame requires a commercial establishment in
 * Germany, which the non-German individual operator does not have. This
 * page is the equivalent transparency disclosure (operator name, country,
 * contact, nature of project) without importing the statutory frame.
 *
 * NOTE on what is and isn't open source: only the @endiagram/mcp NPM
 * client is published as open source (MIT). The verification engine,
 * web applications, and infrastructure are proprietary and operator-
 * owned. Do not claim "open-source project" anywhere on this page or
 * the privacy / terms pages — it is materially misleading and would
 * be a misrepresentation under consumer protection law.
 */
export function LegalPage() {
    return (
        <div className="min-h-screen">
            <LandingHeader />
            <main className="pt-20 sm:pt-24 pb-16 px-4 sm:px-6">
                <article className="max-w-3xl mx-auto">
                    <header className="mb-12">
                        <h1 className="text-3xl sm:text-4xl font-bold mb-3">Legal Notice</h1>
                        <p className="text-sm text-text-muted">
                            Operator information for transparency under EU Directive 2000/31/EC and similar.
                        </p>
                    </header>

                    <div className="space-y-8 text-text-secondary leading-relaxed">
                        <Field label="Operator">Dushyant Suthar</Field>
                        <Field label="Country">India</Field>
                        <Field label="Contact">
                            <a href="mailto:privacy@endiagram.com" className="text-accent hover:underline">
                                privacy@endiagram.com
                            </a>
                        </Field>
                        <Field label="Nature">
                            Developer tool provided free of charge by an individual operator. No goods or
                            services are offered for sale through this website at this time. There is no
                            employer and no company registration behind the operator.
                        </Field>
                        <Field label="Open-source component">
                            Only the <code className="text-accent">@endiagram/mcp</code> NPM client is
                            published as open source (MIT licence) at{' '}
                            <a
                                href="https://github.com/dushyant30suthar/endiagram-mcp"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent hover:underline"
                            >
                                github.com/dushyant30suthar/endiagram-mcp
                            </a>. The verification engine, language specification, theme system, web
                            applications, and infrastructure are proprietary and operator-owned.
                        </Field>
                        <Field label="Infrastructure providers">
                            <ul className="list-disc list-inside space-y-1 mt-1">
                                <li><strong>Cloudflare Pages</strong> — landing site and playground hosting + CDN</li>
                                <li><strong>Railway</strong> — API server hosting</li>
                                <li><strong>npm Registry</strong> — package distribution for the open-source <code className="text-accent">@endiagram/mcp</code> client</li>
                                <li><strong>GitHub</strong> — repository host for the open-source NPM client</li>
                            </ul>
                        </Field>
                        <Field label="Privacy and data handling">
                            See the <a href="/privacy" className="text-accent hover:underline">Privacy Policy</a>{' '}
                            for detailed information on what we log, how we use it, and your rights.
                        </Field>
                        <Field label="Terms of use">
                            See the <a href="/terms" className="text-accent hover:underline">Terms of Use</a>{' '}
                            for the conditions under which the Service is provided.
                        </Field>
                    </div>

                    <p className="mt-12 text-xs text-text-muted/70 leading-relaxed">
                        This page is the equivalent of an Impressum / operator-info disclosure under EU and
                        similar transparency requirements. Endiagram is operated by an individual, not a
                        company; no business address is published because the operator is not required to
                        publish a residential address under GDPR Article 13 — email is sufficient. If you
                        require a physical correspondence address for a specific legal purpose, contact us by
                        email and we will respond appropriately.
                    </p>
                </article>
            </main>
            <LandingFooter />
        </div>
    )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 sm:gap-6">
            <div className="text-[10px] uppercase tracking-widest text-text-muted font-mono pt-1">
                {label}
            </div>
            <div>{children}</div>
        </div>
    )
}
